import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';

import Button from '~/components/base/Button';
import Icon from '~/components/base/Icon';
import Image from '~/components/base/Image';
import WashTypeBadge from '~/components/WashTypeBadge';

import { HiddenSpan } from '~/styles/helpers';
import { h2 } from '~/styles/typography';

import { dynamicFontSize, hexToRgba, formatText } from '~/utilities/helpers';

const Card = styled.div`
    margin: 0;
    padding: 25px;
    position: relative;
    background-color: ${({ theme }) => theme.colors.text.blue};
    color: ${({ theme }) => theme.colors.primary.white};
    border-radius: 53px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    justify-content: space-between;
    text-align: center;
    box-sizing: border-box;
    width: 73vw;
    aspect-ratio: 0.895;
    flex: 0 0 calc(73vw - ${({ theme }) => theme.layout.gutter} * 2);
    box-shadow: 0 2px 8px
        ${({ theme }) => hexToRgba(theme.colors.primary.black, 0.34)};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        padding: 35px;
        text-align: left;
        height: 100%;
        flex: 1 0 45%;
        width: 45%;
        max-width: 709px;
        aspect-ratio: 1.4;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        flex: 1 0 60%;
        width: 100%;
        height: auto;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.headerBreak}) {
        flex: 1 0 100%;
    }
`;

const H2 = styled.h2`
    ${h2};
    font-size: clamp(2.4rem, 2.01vw, 4.2rem);
`;

const CardImage = styled(Image)`
    width: calc(100% - 85px);
    height: auto;
    position: absolute;
    left: 0;
    bottom: 0;
    border-bottom-left-radius: 53px;
    max-height: 50%;

    img {
        object-fit: contain;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        width: 37%;
        max-height: 67%;
    }
`;

const CardContent = styled.div`
    align-self: flex-end;
    display: flex;
    gap: 15px;
    align-items: flex-end;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        max-width: 63%;
    }
`;

const CardDescription = styled.p`
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.primary.white};
    display: none;
    font-size: ${dynamicFontSize(1.4, 2.2, 2.8)};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: block;
        font-size: ${dynamicFontSize(1.4, 2.2, 2.8)};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding-top: 35px;
        font-size: ${dynamicFontSize(1.8, 2.2, 2.8)};
    }
`;

const StyledWashtypeBadge = styled(WashTypeBadge)`
    margin-top: 15px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-top: 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-top: 15px;
    }
`;

const AnythingCard = ({ title, badge = '', description, image, link }) => {
    let cleaningCategories = badge;
    cleaningCategories = cleaningCategories?.split('/');

    return (
        <Card>
            <div>
                <H2>{formatText(title)}</H2>
                {badge && (
                    <StyledWashtypeBadge
                        product={{
                            cleaningCategories: {
                                nodes: cleaningCategories
                                    ? cleaningCategories.map((category) => ({
                                          name: category,
                                          id: kebabCase(category)
                                      }))
                                    : []
                            }
                        }}
                    />
                )}
            </div>
            {image && <CardImage {...image} objectFit="contain" />}
            <CardContent>
                <CardDescription>{formatText(description)}</CardDescription>
                <Button
                    href={link.url}
                    external={link.target === '_blank'}
                    buttonSize="mini"
                >
                    <HiddenSpan>View {title}</HiddenSpan>
                    <Icon icon="arrow" size={16} color="blue" />
                </Button>
            </CardContent>
        </Card>
    );
};

AnythingCard.propTypes = {
    title: PropTypes.string.isRequired,
    badge: PropTypes.string,
    description: PropTypes.string.isRequired,
    image: PropTypes.shape({
        sourceUrl: PropTypes.string.isRequired,
        altText: PropTypes.string.isRequired,
        localFile: PropTypes.shape({
            childImageSharp: PropTypes.shape({
                gatsbyImageData: PropTypes.object.isRequired
            }).isRequired
        })
    }).isRequired,
    link: PropTypes.shape({
        url: PropTypes.string.isRequired,
        target: PropTypes.string
    }).isRequired
};

export default AnythingCard;
